import React, { Dispatch, FC, SetStateAction, useEffect, useState, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import Avatar from 'components/UI/Avatar';
import UILink from 'components/UI/Link';
import VerificationBadge from 'components/Profile/ProfileTop/VerificationBadge';

import { UnionExtraData, UserObjectType } from 'constants/graphqlTypes';
import { transformRoute } from 'helpers/routingHelper';
import usePageType from 'helpers/usePageType';
import getAuthUser from 'helpers/getAuthUser';
import { IContentIds } from 'helpers/useContentReactions';
import { TEST_ACTION_USERNAME } from 'constants/aqa/pickDetails';
import { ROUTE_USER_PROFILE } from 'routes';

import { generateFullName } from 'components/Profile/ProfileTop/helpers';
import { useReactionContext } from 'components/Reactions/hooks';
import { StyledUserInfo } from 'components/UI/Highlight/styled';
import { StyledUsername } from 'components/Sparks/Views/Card/styled';
import ReactionQuickButton from 'components/Reactions/ReactionQuickButton';
import ReactionsToken from 'components/Reactions/ReactionsToken';
import { useHalfTransparency } from './hooks';

import {
  APPEAR_ANIMATION,
  StyledActionContent,
  StyledAvatarWithReactionContainer,
  StyledComment,
  StyledCommentInfo,
  StyledFollowingStatus,
  StyledHidePaddingTop,
  StyledName,
  StyledReactionContainer,
  StyledUserBio,
  StyledAvatarAnimationWrapper,
} from './styled';

interface ICommentProps {
  user: UserObjectType;
  fontColor: string;
  isDetail?: boolean;
  isSmall?: boolean;
  isSpotlight?: boolean;
  isSpotlightInFocus?: boolean;
  hasAnimation: boolean;
  withoutPadding?: boolean;
  isVirtualAction?: boolean;
  action?: UnionExtraData;
  id?: string;
  isReactionButtonMenuShown?: boolean;
  isReactionTokenModalOpen?: boolean;
  setIsReactionButtonMenuShown?: Dispatch<SetStateAction<boolean>>;
  setIsReactionsModalOpen?: Dispatch<SetStateAction<boolean>>;
  setIsReactionTokenModalOpen?: Dispatch<SetStateAction<boolean>>;
}

const Action: FC<ICommentProps> = ({
  children,
  user,
  fontColor,
  isDetail,
  isSmall,
  isSpotlight,
  isSpotlightInFocus,
  hasAnimation,
  withoutPadding,
  action,
  isReactionButtonMenuShown,
  setIsReactionButtonMenuShown,
  isVirtualAction,
  setIsReactionsModalOpen,
  isReactionTokenModalOpen,
  setIsReactionTokenModalOpen,
  id,
}) => {
  const { pickId } = useParams<{ pickId: string }>();
  const location = useLocation();
  const params = useParams<{ feedId?: string; pickId?: string }>();

  // Check if we're on the FeedItem route
  const isFeedItemRoute = Boolean(
    params.feedId && params.pickId && location.pathname.includes(`/feed/${params.feedId}/${params.pickId}`)
  );

  const { isItemDetail, isMyProfile } = usePageType();
  const { username: authenticateUsername } = getAuthUser();
  const { userInfo, username, isFollowedByMe, isVerified } = user;
  const { bio } = userInfo ?? {};
  const isAuthenticateUser = username === authenticateUsername;
  const avatarSize = isSmall ? 22 : 44;
  const isCurrentCardInFocus = isSpotlight || isDetail ? isSpotlightInFocus || pickId === id : true;

  const { isQuickReactionOpen, setPickerId } = useReactionContext();

  const { ref } = useHalfTransparency();
  const { ref: actionRef } = useHalfTransparency();

  const avatarRef = useRef<HTMLDivElement>(null);
  const [shouldAnimateAvatar, setShouldAnimateAvatar] = useState(false);
  const thoughtTextRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPickerId?.(user.id);
  }, [user, setPickerId]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldAnimateAvatar(true);
        }
      },
      {
        rootMargin: '-112px 0px 0px 0px',
        threshold: 0.1,
      }
    );
    if (avatarRef.current) observer.observe(avatarRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
        }
      },
      {
        rootMargin: '-112px 0px 0px 0px',
        threshold: 0.1,
      }
    );
    if (thoughtTextRef.current) observer.observe(thoughtTextRef.current);
    return () => observer.disconnect();
  }, []);

  const { id: actionId } = action ?? {};

  const contentIds: IContentIds =
    action?.__typename === 'CardCommentSchema'
      ? { commentId: action?.id, pickId: id || undefined }
      : { pickId: id || undefined };

  const [shouldRunAnimation, setShouldRunAnimation] = useState(false);

  useEffect(() => {
    let timeout: number;
    if (pickId === id) {
      timeout = +setTimeout(() => {
        setShouldRunAnimation(true);
      }, 5000);
    }
    return () => clearTimeout(timeout);
  }, [pickId, id]);

  return (
    <StyledComment
      className="action-comment-wrap"
      fontColor={fontColor}
      isDetail={isDetail}
      isSpotlight={isSpotlight}
      isQuickReactionOpen={!!isQuickReactionOpen}
      isReactionButtonMenuShown={isReactionButtonMenuShown}
      isReactionTokenModalOpen={isReactionTokenModalOpen}
      hasAnimation={hasAnimation}
      withoutPadding={withoutPadding}
      isCardInFocus={isCurrentCardInFocus}
      animationDuration={isSpotlight ? '10s' : undefined}
    >
      <div>
        <StyledAvatarWithReactionContainer>
          <StyledAvatarAnimationWrapper ref={avatarRef} animate={shouldAnimateAvatar} isFeedItemRoute={isFeedItemRoute}>
            <Avatar user={user} dimension={avatarSize} />
          </StyledAvatarAnimationWrapper>
          {isDetail && (
            <StyledReactionContainer isVirtualAction={isVirtualAction} hasLoadedReactions={shouldRunAnimation}>
              {!isAuthenticateUser && setIsReactionButtonMenuShown && setIsReactionsModalOpen && (
                <ReactionQuickButton
                  className={APPEAR_ANIMATION}
                  offset={16}
                  isReactionButtonMenuShown={!!isReactionButtonMenuShown}
                  setIsReactionButtonMenuShown={setIsReactionButtonMenuShown}
                  isDark
                  entityId={actionId ?? ''}
                  setIsReactionsModalOpen={setIsReactionsModalOpen}
                  isReactionMenuOpened={isQuickReactionOpen}
                />
              )}
              <ReactionsToken
                isDetail={isDetail}
                className={APPEAR_ANIMATION}
                isVertical
                hideSecondEmoji
                contentIds={contentIds}
                isDark
                setIsModalOpen={setIsReactionTokenModalOpen}
              />
            </StyledReactionContainer>
          )}
        </StyledAvatarWithReactionContainer>
        <StyledCommentInfo isSmall={isSmall}>
          <div ref={ref}>
            <StyledUserInfo className="action-user-info">
              <UILink route={transformRoute(ROUTE_USER_PROFILE, { profileName: username })}>
                <StyledName>
                  <StyledUsername data-testid={TEST_ACTION_USERNAME}>{generateFullName(user)}</StyledUsername>
                  {isVerified && <VerificationBadge />}
                  {!isMyProfile && isFollowedByMe && (
                    <StyledFollowingStatus>&nbsp;·&nbsp;Following</StyledFollowingStatus>
                  )}
                </StyledName>
              </UILink>
            </StyledUserInfo>
            {bio && (
              <StyledUserBio className="action-user-info" isSmall={!isItemDetail}>
                {bio}
              </StyledUserBio>
            )}
          </div>
          <StyledActionContent
            isDetail={isDetail}
            ref={actionRef}
            className={username ? 'hide-padding' : StyledHidePaddingTop}
          >
            {children}
          </StyledActionContent>
        </StyledCommentInfo>
      </div>
    </StyledComment>
  );
};

export default Action;
